import { apiClient, handleApiError } from './client';
import type { 
  Service, 
  CreateServiceDto, 
  UpdateServiceDto, 
  Vendor, 
  CreateVendorDto, 
  UpdateVendorDto,
  ServiceStatistics 
} from '@/types/service.types';

export class ServiceService {
  async getEventServices(eventId: number): Promise<Service[]> {
    try {
      const response = await apiClient.get<{ data: Service[] }>(`/services/events/${eventId}`);
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getService(serviceId: number): Promise<Service> {
    try {
      const response = await apiClient.get<{ data: Service }>(`/services/${serviceId}`);
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async createService(eventId: number, serviceData: CreateServiceDto): Promise<Service> {
    try {
      const response = await apiClient.post<{ data: Service }>(`/services/events/${eventId}`, serviceData);
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async updateService(serviceId: number, serviceData: UpdateServiceDto): Promise<Service> {
    try {
      const response = await apiClient.put<{ data: Service }>(`/services/${serviceId}`, serviceData);
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async deleteService(serviceId: number): Promise<void> {
    try {
      await apiClient.delete(`/services/${serviceId}`);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getServiceStatistics(eventId: number): Promise<ServiceStatistics> {
    try {
      const response = await apiClient.get<{ data: ServiceStatistics }>(`/services/events/${eventId}/statistics`);
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Vendor management
  async getAllVendors(): Promise<Vendor[]> {
    try {
      const response = await apiClient.get<{ data: Vendor[] }>('/services/vendors');
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getVendor(vendorId: number): Promise<Vendor> {
    try {
      const response = await apiClient.get<{ data: Vendor }>(`/services/vendors/${vendorId}`);
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async createVendor(vendorData: CreateVendorDto): Promise<Vendor> {
    try {
      const response = await apiClient.post<{ data: Vendor }>('/services/vendors', vendorData);
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async updateVendor(vendorId: number, vendorData: UpdateVendorDto): Promise<Vendor> {
    try {
      const response = await apiClient.put<{ data: Vendor }>(`/services/vendors/${vendorId}`, vendorData);
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async deleteVendor(vendorId: number): Promise<void> {
    try {
      await apiClient.delete(`/services/vendors/${vendorId}`);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Vendor search methods
  async searchVendors(filters: {
    search?: string;
    service_type?: string;
    location?: string;
    min_rating?: number;
    max_rating?: number;
    limit?: number;
  }): Promise<Vendor[]> {
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await apiClient.get<{ data: Vendor[] }>(`/services/vendors/search?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getVendorsByServiceType(serviceType: string): Promise<Vendor[]> {
    try {
      const response = await apiClient.get<{ data: Vendor[] }>(`/services/vendors/service-type/${serviceType}`);
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
}

export const serviceService = new ServiceService();
